import React, { useCallback, useState, useContext } from 'react';
import { useApiFetch } from '../../utils/useApiFetch';
import SkeletonLoader from '../../utils/SkeletonLoader';
import { useDropzone } from 'react-dropzone';
import { TokenContext } from '../../Context/TokenContext';
import { getApiBaseUrl } from '../../utils/Config';
import { AlertContext } from '../../Context/AlertContext';
import { useIsAuthenticated } from "../../utils/msalCompat";
import { CandidateContext } from "../../Context/CandidateContext";

const CandidateProfilePicture = ({ candidateData, profilePic, candidateID }) => {
    const isAuthenticated = useIsAuthenticated();
    const apiFetch = useApiFetch();
    const { idTokenClaims } = useContext(TokenContext);
    const profilepicuri = profilePic && typeof profilePic === 'object' ? profilePic.Uri : profilePic;
    const [preview, setPreview] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const baseUrl = getApiBaseUrl();
    const { refetchCandidateImage } = useContext(CandidateContext);
    const { showAlert } = useContext(AlertContext);

    // Center-crops to a square then resizes to targetSize x targetSize
    const cropAndResizeImage = (file, targetSize = 400) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = targetSize;
                    canvas.height = targetSize;
                    const ctx = canvas.getContext('2d');

                    // White background (handles transparent PNGs)
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, targetSize, targetSize);

                    // Center-crop to square before scaling
                    const srcSize = Math.min(img.width, img.height);
                    const srcX = (img.width - srcSize) / 2;
                    const srcY = (img.height - srcSize) / 2;

                    ctx.drawImage(img, srcX, srcY, srcSize, srcSize, 0, 0, targetSize, targetSize);

                    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
                    resolve(dataUrl.split(',')[1]);
                };
                img.onerror = (err) => reject(err);
            };
            reader.onerror = (err) => reject(err);
            reader.readAsDataURL(file);
        });
    };

    const onFileChange = useCallback((acceptedFiles, fileRejections) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            setSelectedFile(file);
            // Show a cropped square preview immediately so it matches what will be saved
            cropAndResizeImage(file, 400).then((base64) => {
                setPreview(`data:image/jpeg;base64,${base64}`);
            }).catch(() => {
                setPreview(URL.createObjectURL(file));
            });
        }

        if (fileRejections.length > 0) {
            fileRejections.forEach(rejection => {
                if (rejection.errors.some(error => error.code === 'file-too-large')) {
                    showAlert(`File "${rejection.file.name}" is too large. Maximum size is 2MB.`, false);
                } else if (rejection.errors.some(error => error.code === 'file-invalid-type')) {
                    showAlert(`File "${rejection.file.name}" is not a supported image type.`, false);
                } else {
                    showAlert(`File "${rejection.file.name}" was rejected.`, false);
                }
            });
        }
    }, []);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: onFileChange,
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/bmp': ['.bmp']
        },
        multiple: false,
        maxSize: 2 * 1024 * 1024
    });

    const handleCancel = (e) => {
        e.stopPropagation();
        setPreview(null);
        setSelectedFile(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
            showAlert('Please select a file before submitting.', false);
            return;
        }
        setSaving(true);

        try {
            const base64Image = await cropAndResizeImage(selectedFile, 400);
            if (!base64Image) {
                showAlert('Something went wrong with your image. Please try again.', false);
                return;
            }
            const payload = {
                FIleBase64: base64Image,
                CandidateID: candidateID,
                DateTimeCreated: new Date().toISOString(),
                DateTimeModified: new Date().toISOString(),
                CreatedBy: 'Admin',
                ModifiedBy: 'User',
                IsDeleted: false,
                AuthUserID: idTokenClaims.oid,
                AttachmentTypeId: 1,
                FileName: selectedFile.name
            };

            const response = await apiFetch(
                `${baseUrl}/api/Attachment/UploadCandidateAttachment`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                }
            );
            const responseData = await response.json();

            if (responseData.success) {
                showAlert('Profile picture updated successfully.', true);
                setPreview(null);
                setSelectedFile(null);
                refetchCandidateImage();
            } else {
                showAlert(responseData.message || 'Something went wrong. Please try again.', false);
            }
        } catch (error) {
            showAlert('Something went wrong. Please try again.', false);
        } finally {
            setSaving(false);
        }
    };

    if (!isAuthenticated) {
        return <div>Please log in to access the dashboard.</div>;
    }
    if (!candidateData) {
        return <SkeletonLoader />;
    }

    const displaySrc = preview || profilepicuri;

    return (
        <form id="ProfilePic-add-form" onSubmit={handleSubmit}>
            <div className="image-upload" {...getRootProps()}>
                <input name="File" {...getInputProps()} />
                <label htmlFor="file_upload_pp">
                    {displaySrc ? (
                        <div className="PlaceHolder-Sub-PP">
                            <img
                                id="ProPic"
                                src={displaySrc}
                                alt={`${candidateData.FirstName} profile picture`}
                            />
                        </div>
                    ) : (
                        <div className="PlaceHolder-Sub-PP-TX">
                            <div className="initials-placeholder">
                                {candidateData.FirstName[0].toUpperCase()}
                                {candidateData.LastName[0].toUpperCase()}
                            </div>
                        </div>
                    )}
                    <div className="profile-pic-overlay">
                        <i className="bi bi-camera-fill"></i>
                        <span>Change</span>
                    </div>
                </label>
            </div>

            {selectedFile && (
                <div className="profile-pic-actions">
                    <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleCancel} disabled={saving}>
                        Cancel
                    </button>
                </div>
            )}
        </form>
    );
};

export default CandidateProfilePicture;
